namespace Shared

open System
open CustomerDexImaging.Models

type Todo = { Id: Guid; Description: string }

type UsState =
  { State : string
    Abbreviation : string }

  type AccountsReceivableInvoiceDetail = {
    Description: string
    Amount: decimal
  }

  type AccountsReceivableInvoiceHeader = {
    Id: int
    InvoiceNumber: string
    CustomerId: int
    CustomerNumber: string
    CustomerName: string
    CustomerAddress: string
    CustomerCity: string
    CustomerState: string
    CustomerZip: string
    CustomerCountry: string
    RemittanceName: string
    RemittanceAddress: string
    RemittanceCity: string
    RemittanceState: string
    RemittanceZip: string
    RemittanceCountry: string
    Date: DateTime
    DueDate: DateTime
    // Freight: decimal
    // Discount: decimal
    // AdjustedTotal: decimal
    // Due: decimal
    // Tax: decimal
    // Total: decimal
    // IsVoid: bool
    // NoteId: int option
  }

  type AccountsReceivableInvoice = {
    AccountsReceivableInvoiceHeader: AccountsReceivableInvoiceHeader
    AccountsReceivableInvoiceDetails: AccountsReceivableInvoiceDetail list
    PaymentLink: string option
  }

type ContactLookupRequest =
  { Email : string }

type Equipment =
  { Id : int
    Token : string
    EquipmentNumber : string
    SerialNumber : string
    Location : string
    Make : string
    Model : string
    ModelCategoryId : int
    IsColor : bool
    ZipCode : string
    PurchaseOrderNumberRequired : bool }

type EquipmentMeter =
  { MeterId : int
    MeterType : string
    LastReading : int option
    LastReadingDate : DateTime option }


type EquipmentWithMeters =
  { Equipment : Equipment
    Meters : EquipmentMeter list }

type NewMeterReadingRequests =
  { Token : string
    EquipmentNumber : string
    Contact : ContactSelectionInfo
    SendConfirmationEmail : bool
    Readings : NewMeterReadingRequest list }

type PasswordResetRequest =
  { Username : string
    RecaptchaResponse : string }

type LoginRequest =
  { Username : string
    Password : string
    RecaptchaResponse : string }

type LoginType =
  | StandardLogin
  | TempLogin

type LoginResponse =
  { Token: string
    Expires: int64
    Username: string
    LoginType : LoginType
    LinkedCustomersCount : int }

type LoginRefreshResponse =
  { Token: string
    Expires: int64
    Username: string }

type EquipmentLookupRequest =
  { EquipmentOrSerialNumber : string
    ZipCode : string
    RecaptchaResponse : string }

type SupplyShipmentLookupRequest =
  { OrderId : int }

type SalesOrderShipmentInfo =
  { ShipmentDate : DateTime
    TrackingNumbers : string list }

type ServiceCallEtaRequest =
  { CallId : int
    CallNumber : string
    EquipmentNumber : string
    CustomerContactName : string option
    CustomerContactEmail : string }

type ServiceCall =
  { Id : int
    Number : string
    Status : string
    ReceivedDate : DateTime
    Description : string }

type SupplyRequest =
  { EquipmentNumber : string
    Contact : ContactSelectionInfo
    SupplyCategories : SupplyCategory list
    MpsScan : MpsScan option
    PurchaseOrderNumber : string option }

type SupplyHistoryRequest =
  { SupplyCategories : SupplyCategory list }

type SupplyQuoteDetail =
  { QuoteDate : DateTime
    QuoteNumber : string
    Item : string
    Description : string
    Quantity : decimal
    Yield : int }

type SupplyOrderDetail =
  { OrderId : int
    OrderDate : DateTime
    OrderNumber : string
    OrderDescription : string
    OrderTypeDescription : string
    Item : string
    Description : string
    Quantity : decimal
    Shipped : decimal
    Yield : int }

type SupplyOrderInfos =
  { SupplyOrderDetails : SupplyOrderDetail list
    SupplyQuoteDetails : SupplyQuoteDetail list }

type ServiceRequest =
  { EquipmentNumber : string
    Contact : ContactSelectionInfo
    ServiceCallReason : ServiceCallReason
    Description : string
    PurchaseOrderNumber : string option }

type SupplyOrderInfo =
  { OrderNumber : string }

type ServiceCallInfo =
  { CallNumber : string }

type MpsScanRequest =
  { EquipmentId : int
    EquipmentNumber : string
    SerialNumber : string }

  type CustomerIdType =
    | CustomerId of int
    | MasterCustomerId of int

  type ContactLocation =
    { ContactId : int
      CustomerId : CustomerIdType
      CustomerNumber : string
      CustomerName : string
      Address : string
      City : string
      State : string
      Zip : string
      Inherited : bool
      Display : string
      WebFeatures : int option }

module Constants =
  let UsStates =
    [ ("Alabama","AL")
      ("Alaska","AK")
      ("Arizona","AZ")
      ("Arkansas","AR")
      ("California","CA")
      ("Colorado","CO")
      ("Connecticut","CT")
      ("Delaware","DE")
      ("Florida","FL")
      ("Georgia","GA")
      ("Hawaii","HI")
      ("Idaho","ID")
      ("Illinois","IL")
      ("Indiana","IN")
      ("Iowa","IA")
      ("Kansas","KS")
      ("Kentucky","KY")
      ("Louisiana","LA")
      ("Maine","ME")
      ("Maryland","MD")
      ("Massachusetts","MA")
      ("Michigan","MI")
      ("Minnesota","MN")
      ("Mississippi","MS")
      ("Missouri","MO")
      ("Montana","MT")
      ("Nebraska","NE")
      ("Nevada","NV")
      ("New Hampshire","NH")
      ("New Jersey","NJ")
      ("New Mexico","NM")
      ("New York","NY")
      ("North Carolina","NC")
      ("North Dakota","ND")
      ("Ohio","OH")
      ("Oklahoma","OK")
      ("Oregon","OR")
      ("Pennsylvania","PA")
      ("Rhode Island","RI")
      ("South Carolina","SC")
      ("South Dakota","SD")
      ("Tennessee","TN")
      ("Texas","TX")
      ("Utah","UT")
      ("Vermont","VT")
      ("Virginia","VA")
      ("Washington","WA")
      ("West Virginia","WV")
      ("Wisconsin","WI")
      ("Wyoming","WY") ]
    |> List.map (fun (s,a) -> { State = s; Abbreviation = a })

  let recaptchaLoginAction = "login"
  let recaptchaLookupEquipmentAction = "lookupEquipment"
  let recaptchaRequestSuppliesAction = "requestSupplies"
  let recaptchaRequestServiceAction = "requestService"
  let recaptchaSubmitMeterReadingsAction = "submitMeterReadings"

module Todo =
  let isValid (description: string) =
    String.IsNullOrWhiteSpace description |> not
  let create (description: string) =
    { Id = Guid.NewGuid()
      Description = description }

module Route =
  let builder typeName methodName =
    sprintf "/api/%s/%s" typeName methodName

type ITodosApi =
  { getTodos: unit -> Async<Todo list>
    addTodo: Todo -> Async<Todo> }